import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  applyColorScheme,
  getAppearanceState,
  getSystemColorScheme,
  resolveColorScheme,
  saveAppearanceState,
  type AppearanceMode,
  type ColorSchemeName,
} from "@/lib/appearance-storage";

type Theme = ColorSchemeName;

type ThemeProviderProps = {
  children: React.ReactNode;
  /** Mantido por compatibilidade; o tema claro é o padrão quando `allowDark` é falso. */
  defaultTheme?: Theme;
  /** Mantido por compatibilidade; a preferência fica em `sysigreja.appearance`. */
  storageKey?: string;
  /**
   * Habilita tema escuro (área logada). Sem ele, o provider força o tema claro —
   * landing, inscrições, sites de igreja e telas de login continuam sempre claros.
   */
  allowDark?: boolean;
};

type ThemeProviderState = {
  /** Scheme efetivo aplicado no `<html>`. */
  theme: Theme;
  isDark: boolean;
  /** Preferência do usuário (system | light | dark). */
  mode: AppearanceMode;
  setMode: (mode: AppearanceMode) => void;
  /** Se o tema escuro está disponível neste contexto (flag + área logada). */
  darkAvailable: boolean;
  /** Compat: equivale a `setMode(theme)`. */
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "light",
  isDark: false,
  mode: "light",
  setMode: () => null,
  darkAvailable: false,
  setTheme: () => null,
};

const noop = () => undefined;

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({ children, allowDark = false }: ThemeProviderProps) {
  const [mode, setModeState] = useState<AppearanceMode>(() => getAppearanceState().mode);
  const [systemScheme, setSystemScheme] = useState<ColorSchemeName>(getSystemColorScheme);

  useEffect(() => {
    if (!allowDark || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setSystemScheme(mediaQuery.matches ? "dark" : "light");
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [allowDark]);

  const theme: Theme = allowDark ? resolveColorScheme(mode, systemScheme) : "light";

  // Antes do paint: aplica o scheme no mesmo commit do React (evita um frame claro).
  // Restaura o scheme anterior ao desmontar: sair da área logada volta ao claro, e um
  // provider público aninhado (ex.: preview) não deixa o `<html>` preso no claro.
  useLayoutEffect(() => {
    const previous: Theme = document.documentElement.classList.contains("dark") ? "dark" : "light";
    applyColorScheme(theme);
    return () => applyColorScheme(previous);
  }, [theme]);

  // Só persiste "disponível" quando a área logada realmente liberou o escuro.
  // Providers públicos (login, inscrição) não podem apagar o hint do próximo boot.
  useLayoutEffect(() => {
    if (!allowDark) return;
    const current = getAppearanceState();
    if (current.darkAvailable === true && current.mode === mode) return;
    saveAppearanceState({ ...current, mode, darkAvailable: true });
  }, [allowDark, mode]);

  const setMode = useCallback((next: AppearanceMode) => {
    setModeState(next);
    saveAppearanceState({ ...getAppearanceState(), version: 1, mode: next, darkAvailable: true });
  }, []);

  const value = useMemo<ThemeProviderState>(
    () => ({
      theme,
      isDark: theme === "dark",
      mode: allowDark ? mode : "light",
      setMode: allowDark ? setMode : noop,
      darkAvailable: allowDark,
      setTheme: allowDark ? setMode : noop,
    }),
    [theme, allowDark, mode, setMode],
  );

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
